import { FC, ReactNode, useEffect, useMemo } from "react";
import { downloadExcel } from "react-export-table-to-excel";
import { Button, DatePicker, Form, Popover, Select, Space } from "antd";
import moment from "moment";
import { useLocation } from "react-router-dom";

import { isDateInRange } from "../../../../helper/dateTimeHelper";
import { DateFormat } from "../../../../constants";
import { compareRequestStatus } from "../../utils";

const { RangePicker } = DatePicker;

interface ExportFileExcelForm {
  createdDateTime: [moment.Moment, moment.Moment] | null;
  eRequestStatus: string | null;
  eWarningStatus: string | null;
}

interface ExportFileExcelProps {
  data: Array<any>;
  initialValues?: any;
  exportContent: {
    fileName: string;
    sheet?: string;
    content: Array<{ header: string; dataIndex: string }>;
  };
  requestStatusOptions: Array<{ label: string; value: string }>;
  onChange?: (changedValues: any, allValues: ExportFileExcelForm) => void;
  children?: ReactNode;
}

const WarningOptions: Array<{ label: string; value: string }> = [
  { label: "Sắp hết hạn", value: "expired" },
  { label: "Tự động gia hạn", value: "autoRenew" },
];

export const ExportFileExcel: FC<ExportFileExcelProps> = ({
  data,
  initialValues = {},
  exportContent,
  requestStatusOptions,
  onChange = () => {},
  children,
}) => {
  const { search } = useLocation();
  const [form] = Form.useForm<ExportFileExcelForm>();

  const eRequestStatus = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return searchParams.get("status") || null;
  }, [search]);

  useEffect(() => {
    form.setFieldValue("eRequestStatus", eRequestStatus);
    onChange({}, { eRequestStatus: eRequestStatus, createdDateTime: null, eWarningStatus: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eRequestStatus, form]);

  const onSubmit = (values: ExportFileExcelForm) => {
    let result = data;

    if (values?.eRequestStatus) {
      result = result.filter((e) => {
        return compareRequestStatus(e.eRequestStatus, values?.eRequestStatus || "");
      });
    }
    if (values?.createdDateTime) {
      const [startDate, endDate] = values.createdDateTime;
      result = result.filter((e) => {
        const createdDate = moment(e.createdDateTime, DateFormat.DD_MM_YYYY);
        return isDateInRange(createdDate, startDate.startOf("day"), endDate.endOf("day"));
      });
    }
    if (values?.eWarningStatus) {
      result = result.filter((record) => {
        const content = record.eContent ? JSON.parse(record.eContent) : null;
        if (content == null || !content[0]?.expiredDate) return false;

        const expiredDate = moment(content[0].expiredDate);
        const maxDate = moment().add(8, "days");

        var isExpired = expiredDate.isBefore(maxDate, "day");

        if (values.eWarningStatus === "autoRenew") return isExpired && record.isAutoRenew;
        return isExpired;
      });
    }

    const body = result.map((e) => {
      let obj: any = {};
      exportContent.content
        .map((e) => e.dataIndex)
        .forEach((key, i) => {
          obj[i] = e[key];
        });
      return obj;
    });

    downloadExcel({
      fileName: exportContent.fileName,
      sheet: exportContent?.sheet || "Sheet 1",
      tablePayload: {
        header: exportContent.content.map((e) => e.header),
        body,
      },
    });
  };

  const content = (
    <Form
      form={form}
      onFinish={onSubmit}
      initialValues={{ ...initialValues, eRequestStatus }}
      onValuesChange={onChange}
    >
      <Space direction="vertical" className="flex">
        <Space direction="vertical" className="flex">
          <div>Ngày tạo:</div>
          <Form.Item name="createdDateTime" style={{ marginBottom: "5px" }}>
            <RangePicker format={DateFormat.DD_MM_YYY} placeholder={["Ngày bắt đầu", "Ngày kết thúc"]} />
          </Form.Item>
        </Space>
        <Space direction="vertical" className="flex">
          <div>Trạng thái:</div>
          <Form.Item name="eRequestStatus" style={{ marginBottom: "5px" }}>
            <Select allowClear className="w-full" placeholder="Chọn trạng thái" options={requestStatusOptions} />
          </Form.Item>
        </Space>
        <Space direction="vertical" className="flex">
          <div>Cảnh báo:</div>
          <Form.Item name="eWarningStatus" style={{ marginBottom: "5px" }}>
            <Select allowClear className="w-full" placeholder="Chọn cảnh báo" options={WarningOptions} />
          </Form.Item>
        </Space>
        <Form.Item noStyle>
          <Button block htmlType="submit" type="primary">
            Xuất excel
          </Button>
        </Form.Item>
      </Space>
    </Form>
  );

  return (
    <Popover trigger={["click"]} placement="left" content={content}>
      {children}
    </Popover>
  );
};
